import React, { useState, useEffect } from 'react';
import { useLogto } from '@logto/react';
import { Download, Users, AlertCircle, FileSpreadsheet, Eye, Code, Home } from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const { getAccessToken, getIdTokenClaims } = useLogto();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [organizationInfo, setOrganizationInfo] = useState(null);
  const [hasAdminPermission, setHasAdminPermission] = useState(false);
  const [error, setError] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [adminToken, setAdminToken] = useState(null);

  // 检查管理员权限并加载数据
  useEffect(() => {
    const checkAdminPermission = async () => {
      try {
        const claims = await getIdTokenClaims();
        const organizations = claims?.organizations || [];
        
        console.log('用户组织信息:', organizations);
        
        let orgId = null;
        let token = null;
        
        if (organizations.length === 0) {
          console.warn('⚠️ 用户未加入组织，将使用 API 资源 token');
          setOrganizationInfo({
            id: null,
            name: '所有用户（无组织）',
          });
          token = await getAccessToken('https://magicpage.cflpai.com');
        } else {
          orgId = organizations[0];
          setOrganizationInfo({
            id: orgId,
            name: '组织管理',
          });
          token = await getAccessToken('urn:logto:resource:organizations', orgId);
        }
        
        if (!token) {
          setError('无法获取权限令牌');
          setLoading(false);
          return;
        }
        setAdminToken(token);

        const [, payload] = token.split('.');
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        const scopes = decoded?.scope ? decoded.scope.split(' ') : [];
        
        console.log('用户权限:', scopes);
        
        const isAdmin = scopes.includes('manage:member') || scopes.includes('view:logs');
        setHasAdminPermission(isAdmin);
        
        if (!isAdmin) {
          setError('您没有管理员权限。需要 manage:member 或 view:logs 权限。');
          setLoading(false);
          return;
        }

        // 加载组织所有成员的历史记录
        await loadOrganizationHistory(orgId, token);
      } catch (err) {
        console.error('检查权限失败:', err);
        setError('权限检查失败: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    checkAdminPermission();
  }, [getIdTokenClaims, getAccessToken]);

  // 加载组织所有成员的历史记录
  const loadOrganizationHistory = async (organizationId, token) => {
    try {
      console.log('加载组织历史记录，组织ID:', organizationId || 'null（所有用户）');
      
      const orgIdParam = organizationId || 'null';
      const response = await axios.get(`/api/admin/organization/${orgIdParam}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHistoryRecords(response.data.records || []);
      console.log('✅ 成功加载历史记录:', response.data.records?.length || 0);
    } catch (err) {
      console.error('加载历史记录失败:', err);
      if (err.response) {
        console.error('错误详情:', err.response.data);
        setError(`加载历史记录失败: ${err.response.data.message || err.response.data.error || err.message}`);
      } else {
        setError('加载历史记录失败: ' + err.message);
      }
      setHistoryRecords([]);
    }
  };

  // 导出为Excel
  const exportToExcel = () => {
    try {
      // 准备Excel数据
      const excelData = historyRecords.map((record, index) => ({
        '序号': index + 1,
        '用户名': record.username || record.name || '未知用户',
        '网页内容描述': record.prompt || record.description || '无描述',
        '专业版式选择': record.layout || '未指定',
        '专业配色方案': record.colors || '未指定',
        'HTML源码': '请使用“下载”按钮按需导出',
        '操作时间': record.created_at ? new Date(record.created_at).toLocaleString('zh-CN') : '未知',
        '生成耗时(秒)': record.generation_time ? (record.generation_time / 1000).toFixed(2) : '未知',
        '文件大小(KB)': record.file_size ? (record.file_size / 1024).toFixed(2) : '未知',
      }));

      // 创建工作簿
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // 设置列宽
      ws['!cols'] = [
        { wch: 6 },   // 序号
        { wch: 15 },  // 用户名
        { wch: 40 },  // 网页内容描述
        { wch: 20 },  // 专业版式选择
        { wch: 20 },  // 专业配色方案
        { wch: 60 },  // HTML源码
        { wch: 20 },  // 操作时间
        { wch: 15 },  // 生成耗时
        { wch: 15 },  // 文件大小
      ];

      // 添加工作表到工作簿
      XLSX.utils.book_append_sheet(wb, ws, '历史记录');

      // 生成文件名
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `组织历史记录_${timestamp}.xlsx`;

      // 导出文件
      XLSX.writeFile(wb, filename);
      
      console.log('✅ 成功导出Excel文件:', filename);
    } catch (err) {
      console.error('导出Excel失败:', err);
      alert('导出失败: ' + err.message);
    }
  };

  // 按需拉取单条记录 HTML（避免列表接口携带大字段）
  const fetchRecordHtml = async (recordId) => {
    if (!adminToken) {
      throw new Error('认证令牌缺失，请刷新页面后重试');
    }
    const orgId = organizationInfo?.id || null;
    const response = await axios.get(`/api/admin/record/${recordId}/html`, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
        // 组织 token 未必带 organizations[]，显式传组织 ID 供后端鉴权
        ...(orgId ? { 'X-Organization-Id': orgId } : {}),
      },
    });
    return response.data?.generated_html || '';
  };

  // 预览HTML
  const handlePreviewHtml = async (recordId) => {
    try {
      const html = await fetchRecordHtml(recordId);
      if (!html) {
        alert('该记录无HTML内容');
        return;
      }
      setPreviewHtml(html);
      setShowPreview(true);
    } catch (err) {
      console.error('预览HTML失败:', err);
      alert('预览失败: ' + (err.response?.data?.error || err.message));
    }
  };

  // 下载单个HTML
  const handleDownloadHtml = async (recordId) => {
    try {
      const html = await fetchRecordHtml(recordId);
      if (!html) {
        alert('该记录无HTML内容');
        return;
      }
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `generated_${recordId}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('下载HTML失败:', err);
      alert('下载失败: ' + (err.response?.data?.error || err.message));
    }
  };

  // 返回主页：容器/Nginx 场景下优先使用硬跳转，避免仅前端路由无响应
  const goHome = () => {
    try {
      navigate('/');
      setTimeout(() => {
        if (window.location.pathname === '/admin') {
          window.location.assign('/');
        }
      }, 120);
    } catch (_e) {
      window.location.assign('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !hasAdminPermission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">访问受限</h2>
          <p className="text-gray-600 mb-6">
            {error || '您没有访问此页面的权限'}
          </p>
          <button
            onClick={goHome}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">组织管理</h1>
                <p className="text-sm text-gray-500">
                  {organizationInfo?.name || '组织管理员面板'} - 共 {historyRecords.length} 条记录
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={goHome}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Home className="w-4 h-4" />
                <span>返回主页</span>
              </button>
              <button
                onClick={exportToExcel}
                disabled={historyRecords.length === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span>导出Excel</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 主内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {historyRecords.length === 0 ? (
            <div className="p-16 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg">暂无历史记录</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      序号
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      用户名
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      网页内容描述
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      专业版式选择
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      专业配色方案
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      HTML源码
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作时间
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {historyRecords.map((record, index) => (
                    <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {record.username || record.name || '未知用户'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 max-w-md">
                        <div className="line-clamp-3" title={record.prompt || record.description}>
                          {record.prompt || record.description || '无描述'}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {record.layout || '未指定'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {record.colors || '未指定'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {record.has_html ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handlePreviewHtml(record.id)}
                              className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                              title="预览HTML"
                            >
                              <Eye className="w-4 h-4" />
                              <span>预览</span>
                            </button>
                            <button
                              onClick={() => handleDownloadHtml(record.id)}
                              className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                              title="下载HTML"
                            >
                              <Download className="w-4 h-4" />
                              <span>下载</span>
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">无</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.created_at ? new Date(record.created_at).toLocaleString('zh-CN') : '未知'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* HTML预览模态框 */}
      {showPreview && previewHtml && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-5/6 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">HTML预览</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                title="HTML预览"
                srcDoc={previewHtml}
                className="w-full h-full border-0"
                sandbox="allow-same-origin allow-scripts"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
